import type { PlanningConstraints } from '@app/core/planning/planning.types';
import { BooleanInput } from '@angular/cdk/coercion';
import type { GanttDataTask } from '@app/services/gantt.service';
import { GanttConfigOptions as DhtmlxGanttConfigOptions, Link, NewTask, Task, ZoomLevel } from '../../../../assets/js/codebase/dhtmlxgantt';

export interface GanttSetup {
    data: GanttTask[];
    links: Link[];
    resources?: MachineResource[];
    constraints?: PlanningConstraints | null;
    options: PartialGanttOptions | GanttBackendOptions;
}

export const GANTT_OPTION_OVERRIDES = Symbol('ganttOptionOverrides');

export interface GanttOptions {
    /** Internal model hook: defaults are available to callers without masking colschema. */
    [GANTT_OPTION_OVERRIDES]?: () => PartialGanttOptions;
    /** Model remember ID; an explicit ttRememberId input takes precedence. */
    rememberId?: string;
    dataTask?: GanttDataTask;
    save: GanttSaveOptions;
    toolbar: GanttToolbarOptions;
    columns: GanttColumn[];
    resources: GanttResourceOptions;
    autoScheduling: GanttAutoSchedulingOptions;
    zoom: GanttZoomOptions;
    config: GanttConfigOptions;
    editing: GanttEditableOptions;

    api?: GanttApi;
}

export interface GanttApi {
    read(loadSchema?: boolean): void;
}

export type PartialGanttOptions = Omit<Partial<GanttOptions>, 'config' | 'editing' | 'resources' | 'autoScheduling' | 'zoom' | 'save' | 'toolbar'> & {
    save?: Partial<GanttSaveOptions>;
    toolbar?: Partial<GanttToolbarOptions>;
    config?: GanttConfigOptions;
    editing?: Partial<GanttEditableOptions>;
    resources?: Partial<GanttResourceOptions>;
    autoScheduling?: Partial<GanttAutoSchedulingOptions>;
    zoom?: Partial<GanttZoomOptions>;
};

export interface GanttToolbarOptions {
    enabled: boolean;
    columns: boolean;
    /** Show the ABC toggle for translated labels/property keys. */
    headers: boolean;
    reload: boolean;
    save: boolean;
    criticalPath: boolean;
    resourceGraph: boolean;
    planningDirection: boolean;
}

export interface GanttSaveOptions {
    autoSave: boolean;
    readAfterSave: boolean;
}

export type DhtmlxGanttConfigKey =
    | 'auto_scheduling'
    | 'auto_scheduling_compatibility'
    | 'auto_scheduling_descendant_links'
    | 'auto_scheduling_initial'
    | 'auto_scheduling_move_projects'
    | 'auto_scheduling_project_constraint'
    | 'auto_scheduling_strict'
    | 'auto_scheduling_use_progress'
    | 'auto_types'
    | 'autofit'
    | 'autoscroll'
    | 'autoscroll_speed'
    | 'autosize'
    | 'autosize_min_width'
    | 'bar_height'
    | 'bar_height_padding'
    | 'baselines'
    | 'branch_loading'
    | 'branch_loading_property'
    | 'buttons_left'
    | 'buttons_right'
    | 'calendar_property'
    | 'cascade_delete'
    | 'click_drag'
    | 'constraint_types'
    | 'container_resize_method'
    | 'container_resize_timeout'
    | 'correct_work_time'
    | 'csp'
    | 'date_format'
    | 'date_grid'
    | 'deadlines'
    | 'deepcopy_on_parse'
    | 'details_on_create'
    | 'details_on_dblclick'
    | 'drag_lightbox'
    | 'drag_links'
    | 'drag_mode'
    | 'drag_move'
    | 'drag_multiple'
    | 'drag_progress'
    | 'drag_project'
    | 'drag_resize'
    | 'drag_timeline'
    | 'duration_step'
    | 'duration_unit'
    | 'dynamic_resource_calendars'
    | 'editable_property'
    | 'editor_types'
    | 'end_date'
    | 'external_render'
    | 'fit_tasks'
    | 'grid_elastic_columns'
    | 'grid_resizer_column_attribute'
    | 'grid_width'
    | 'highlight_critical_path'
    | 'horizontal_scroll_key'
    | 'inherit_calendar'
    | 'inherit_scale_class'
    | 'initial_scroll'
    | 'inline_editors_date_processing'
    | 'inline_editors_multiselect_open'
    | 'keep_grid_width'
    | 'keyboard_navigation'
    | 'keyboard_navigation_cells'
    | 'layer_attribute'
    | 'layout'
    | 'lightbox'
    | 'lightbox_additional_height'
    | 'link_arrow_size'
    | 'link_attribute'
    | 'link_line_width'
    | 'link_radius'
    | 'link_wrapper_width'
    | 'links'
    | 'min_column_width'
    | 'min_duration'
    | 'min_grid_column_width'
    | 'min_task_grid_row_height'
    | 'multiselect'
    | 'multiselect_one_level'
    | 'open_split_tasks'
    | 'open_tree_initially'
    | 'order_branch'
    | 'order_branch_free'
    | 'placeholder_task'
    | 'preserve_scroll'
    | 'process_resource_assignments'
    | 'project_end'
    | 'project_start'
    | 'quick_info_detached'
    | 'quickinfo_buttons'
    | 'readonly'
    | 'readonly_property'
    | 'redo'
    | 'reorder_grid_columns'
    | 'resize_rows'
    | 'resource_assignment_store'
    | 'resource_attribute'
    | 'resource_calendars'
    | 'resource_property'
    | 'resource_render_empty_cells'
    | 'resource_store'
    | 'root_id'
    | 'round_dnd_dates'
    | 'row_height'
    | 'rtl'
    | 'scale_height'
    | 'scale_offset_minimal'
    | 'scales'
    | 'schedule_from_end'
    | 'scroll_on_click'
    | 'scroll_size'
    | 'select_task'
    | 'server_utc'
    | 'show_chart'
    | 'show_empty_state'
    | 'show_errors'
    | 'show_grid'
    | 'show_links'
    | 'show_markers'
    | 'show_progress'
    | 'show_quick_info'
    | 'show_task_cells'
    | 'show_tasks_outside_timescale'
    | 'show_unscheduled'
    | 'skip_off_time'
    | 'smart_rendering'
    | 'smart_scales'
    | 'sort'
    | 'start_date'
    | 'start_on_monday'
    | 'static_background'
    | 'static_background_cells'
    | 'task_attribute'
    | 'task_date'
    | 'task_grid_row_resizer_attribute'
    | 'task_scroll_offset'
    | 'time_picker'
    | 'time_step'
    | 'timeline_placeholder'
    | 'tooltip_hide_timeout'
    | 'tooltip_offset_x'
    | 'tooltip_offset_y'
    | 'tooltip_timeout'
    | 'touch'
    | 'touch_drag'
    | 'touch_feedback'
    | 'touch_feedback_duration'
    | 'type_renderers'
    | 'types'
    | 'undo'
    | 'undo_actions'
    | 'undo_steps'
    | 'undo_types'
    | 'wai_aria_attributes'
    | 'wheel_scroll_sensitivity'
    | 'wide_form'
    | 'work_time';

export type NativeGanttConfigOptions = Partial<Pick<DhtmlxGanttConfigOptions, DhtmlxGanttConfigKey>>;

export interface GanttConfigOptions extends NativeGanttConfigOptions {
    /**
     * Backwards-compatible aliases for dhtmlxGantt config.highlight_critical_path and config.multiselect.
     */
    criticalPath?: boolean;
    multiSelect?: boolean;
    [customConfig: string]: any;
}

export interface GanttResourceOptions {
    enabled: boolean;
    showSum: boolean;
    columns: GanttColumn[];
    config?: DhtmlxGanttConfigOptions['resources'];
}

export interface GanttAutoSchedulingOptions {
    enabled: boolean;
    showConstraints?: boolean;
    applyConstraints?: boolean;
    gapBehavior?: 'preserve' | 'compress';
    descendantLinks?: boolean;
    scheduleOnParse?: boolean;
    moveProjects?: boolean;
    useProgress?: boolean;
    scheduleFromEnd?: boolean;
    projectConstraint?: boolean;
    compatibility?: boolean;
    strict?: boolean;
    config?: DhtmlxGanttConfigOptions['auto_scheduling'];
}

export interface GanttEditableOptions {
    links: boolean;
    tasks: boolean;
    drag: GanttDragOptions;
}

export interface GanttDragOptions {
    links?: BooleanInput;
    move?: BooleanInput;
    resize?: BooleanInput;
    progress?: BooleanInput;
    multiple?: BooleanInput | null;
    project?: BooleanInput | null;
    lightbox?: BooleanInput | null;
}

export interface GanttZoomOptions {
    levels: ZoomLevel[];
    activeLevel: number | string;
    trigger: string | null | undefined;
    useKey?: string;
    widthStep?: number;
    minColumnWidth?: number;
    maxColumnWidth?: number;
}

export interface GanttRememberState {
    version: 1;
    columns?: GanttRememberColumnState;
    resourceColumns?: GanttRememberColumnState;
    view?: GanttRememberViewState;
    zoomLevel?: number | string;
    timelineCenterDate?: string;
    /** Stable provider row identity; null records an explicitly cleared dependency path. */
    dependencyPathRootUuid?: string | null;
    /** Ordered column priorities; a single object is accepted for older preferences. */
    sort?: GanttSortState | GanttSortState[];
    resourceSort?: GanttSortState | GanttSortState[];
    gridWidth?: number;
    sidePanel?: GanttRememberSidePanelState;
    updatedAt?: string;
}

export interface GanttSortState {
    propertyKey: string;
    direction: 'asc' | 'desc';
}

export interface GanttRememberViewState {
    criticalPath?: boolean;
    resourceGraph?: boolean;
    backwardPlanning?: boolean;
}

export interface GanttRememberColumnState {
    orderedPropertyKeys?: string[];
    widths?: Record<string, number | string>;
    /** Older saved values are ignored; current column options control visibility. */
    hiddenPropertyKeys?: string[];
    /** Explicit user choices; absent keys continue to use the current schema. */
    visibility?: Record<string, boolean>;
}

export interface GanttRememberSidePanelState {
    open?: boolean;
    width?: number;
    [key: string]: any;
}

export interface GanttBackendOptions extends PartialGanttOptions {
    keyno?: string | number;
    edit_links?: BooleanInput;
    edit_tasks?: BooleanInput;
    critical_path?: BooleanInput;
    multi_select?: BooleanInput;
    multiselect?: BooleanInput;
    resources_enabled?: BooleanInput;
    resource_columns?: GanttColumn[];
    auto_scheduling_enabled?: BooleanInput;
    auto_scheduling_show_constraints?: BooleanInput;
    auto_scheduling_apply_constraints?: BooleanInput;
    auto_scheduling_gap_behavior?: 'preserve' | 'compress';
    auto_scheduling_descendant_links?: BooleanInput;
    auto_scheduling_schedule_on_parse?: BooleanInput;
    auto_scheduling_initial?: BooleanInput;
    auto_scheduling_move_projects?: BooleanInput;
    auto_scheduling_use_progress?: BooleanInput;
    auto_scheduling_schedule_from_end?: BooleanInput;
    auto_scheduling_project_constraint?: BooleanInput;
    auto_scheduling_compatibility?: BooleanInput;
    auto_scheduling_strict?: BooleanInput;
    [nativeConfigKey: string]: any;
}

export interface GanttColumn {
    propertyKey: string;
    label?: string;
    name?: string;
    hidden?: BooleanInput;
    hide?: BooleanInput;
    tree?: BooleanInput | number;
    width?: number | string;
    minWidth?: number;
    maxWidth?: number;
    align?: 'left' | 'center' | 'right';
    resize?: BooleanInput | number;
    sort?: boolean | string;
    /** Show a local text filter beneath this column header. Matches original and formatted values. */
    floatingFilter?: BooleanInput | number;
    /** Same SQL type names as grid columns. Absent types are inferred from actual values. */
    coltype?: string | null;
    /** Fixed decimal places for numeric columns; zero is valid. */
    decimals?: number | null;
    /** Angular date format; defaults to the user's date format, with time for datetime. */
    dateFormat?: string | null;
    /** Enable a column action. DataTask 4067 resolves it when state is absent. */
    clickType?: GanttCellActionType | null;
    doubleClickType?: GanttCellActionType | null;
    component?: 'StateModal' | 'StateDialog' | null;
    /** Static destination and ready-to-use parameters supplied by colschema. */
    state?: string | null;
    stateParams?: string | null;
    readOnClose?: BooleanInput | number;
    rebindOnClose?: BooleanInput | number;
    [columnConfigKey: string]: any;
}

export type GanttCellActionType = 'default' | 'modal' | 'popup';

export type GanttChangeEvent = 'autoschedule' | 'drag' | 'move' | 'update' | 'add' | 'delete';

export interface GanttTaskChangeInfo {
    /** Stable save identity, independent of the widget's id. */
    _uuid?: string | number;
    event: GanttChangeEvent;
    dragMode: 'resize' | 'progress' | 'move' | 'ignore' | null;
    task: GanttTask | null;
    entity: 'link' | 'task';
    id: string | number;
    parentId: string | number | null;
    link: GanttLink | null;
    predecessor: Task | null;
}

export interface GanttPlannedSegment {
    start_date: Date | string | null;
    end_date: Date | string | null;
    hours_planned: number | null;
    [key: string]: unknown;
}

export type GanttTask = NewTask & {
    /** Provider row key; new tasks receive a frontend UUID until refresh. */
    _uuid?: string | number;
    owner?: string | number;
    capacity?: number;
    capacityHoursPerDay?: number;
    /** Source booking slots; the graph previews them at current task dates. Empty means no booked work. */
    planned_segments?: GanttPlannedSegment[];

    [key: string]: any;
};

export interface GanttLink extends Link {
    _uuid?: string | number;
}

export type MachineResource = {
    [propertyKey: string]: unknown;
    id: string | number;
    text: string;
    /** Omitted when the provider has no availability data. */
    capacity?: number;
    /** Frontend total row; never a task owner or provider resource. */
    isGanttResourceSum?: boolean;
};
